import type { AdminReportDetailDto } from '@carcheck/shared';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';
import { formatDateTime } from '../../../utils/format';
import { describeAcceptance } from '../_lib/reportAcceptance';

/**
 * Bottom bar of the report detail drawer (AUT-265).
 *
 * Replaces `ReportDetailFooter`, which held the approve / on-revision /
 * reject buttons against three endpoints that now 404. The bar is NOT an
 * action bar any more — an action bar with nothing in it but an
 * audit-log note is dead chrome. It carries the one fact the tabs never
 * showed and an adjudicator always needs: whether the customer accepted
 * the report, when, and whether they accepted it or the 24h timer did.
 *
 * Read-only by construction: no mutation hook is imported here, and the
 * admin panel no longer holds any endpoint that writes a report.
 */
export interface ReportAcceptanceBarProps {
  detail: AdminReportDetailDto;
}

const TONE_COLOR = {
  success: tokens.state.success,
  waiting: tokens.state.warning,
  neutral: tokens.text.tertiary,
} as const;

export const ReportAcceptanceBar = ({ detail }: ReportAcceptanceBarProps): ReactElement => {
  const { t } = useTranslation();
  const acceptance = describeAcceptance(detail);
  const color = TONE_COLOR[acceptance.tone];

  const Icon =
    acceptance.tone === 'success'
      ? CheckCircleOutlineIcon
      : acceptance.tone === 'waiting'
        ? HourglassEmptyIcon
        : HistoryIcon;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: '14px 24px',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Icon sx={{ fontSize: 18, color }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: tokens.text.tertiary,
          }}
        >
          {t('reports.acceptance.label')}
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color }}>
          {t(`reports.acceptance.${acceptance.state}`, {
            at: acceptance.at === null ? '' : formatDateTime(acceptance.at),
          })}
        </Typography>
      </Box>
    </Box>
  );
};
